import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import {
  FaArrowRight,
  FaBuilding,
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaChevronRight,
  FaClock,
  FaHeart,
  FaHeadset,
  FaLaptopHouse,
  FaMapMarkerAlt,
  FaMicrophone,
  FaShieldAlt,
  FaStar,
  FaTag,
  FaThLarge,
  FaUsers,
  FaVideo,
  FaLock,
} from "react-icons/fa";
import Navbar from "../../components/Navbar";
import LoginModal from "../../components/LoginModal";
import MeetingSpacePopup from "../../components/MeetingSpacePopup";
import { roomsAPI } from "../../services/meetingsAPI";
import { getImageUrl, getExternalImageProps } from "../../utils/imageHelper";
import { filterMeetingRoomsVisibleToUser, isExternalMeetingRoom } from "../../utils/meetingRoomVisibility";
import { mergeUserWithStaffSession } from "../../utils/companyStaffSession";
import "react-datepicker/dist/react-datepicker.css";
import "./MeetingHome.css";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1400&q=80";

const popularCities = [
  "Mumbai",
  "Bangalore",
  "Delhi",
  "Hyderabad",
  "Chennai",
  "Pune",
  "Kolkata",
];

const featureItems = [
  {
    title: "Instant Booking",
    description: "Book your space in just a few clicks.",
    icon: <FaCalendarAlt />,
  },
  {
    title: "Verified Spaces",
    description: "All rooms are checked for quality and safety.",
    icon: <FaShieldAlt />,
  },
  {
    title: "Best Price Guarantee",
    description: "Transparent rates with no hidden charges.",
    icon: <FaTag />,
  },
  {
    title: "24/7 Support",
    description: "Our team is always ready to help.",
    icon: <FaHeadset />,
  },
];

const categoryItems = [
  { label: "Conference", type: "Conference Room", icon: <FaUsers /> },
  { label: "Coworking", type: "Coworking Space", icon: <FaLaptopHouse /> },
  { label: "Events", type: "Event Space", icon: <FaBuilding /> },
  { label: "Training", type: "Training Room", icon: <FaChalkboardTeacher /> },
  { label: "Board Room", type: "Board Room", icon: <FaBuilding /> },
  { label: "Podcast", type: "Podcast Studio", icon: <FaMicrophone /> },
  { label: "Webinar", type: "Webinar Room", icon: <FaVideo /> },
];

const trustedCompanies = ["Infosys", "Wipro", "TATA", "IBM", "Accenture", "Google", "Microsoft"];

const sessionOptions = ["Hourly", "Half Day", "Full Day"];
const durationOptions = ["1 hour", "2 hours", "4 hours", "8 hours"];
const roomTypeOptions = [
  "Any type",
  "Conference Room",
  "Board Room",
  "Training Room",
  "Coworking Space",
  "Event Space",
];

const specialOffers = [
  {
    id: "weekend",
    badge: "Flat 30% OFF",
    title: "Weekend Bookings",
    description: "Book any premium meeting room this weekend and save big.",
    image: "https://images.unsplash.com/photo-1517502884422-41eaead166d4?w=900&q=80",
  },
];

const fallbackRecommendedSpaces = [];

const isUserAddedMeetingRoom = (room) => {
  if (!room || isExternalMeetingRoom(room)) return false;
  const source = String(room.source || "local").toLowerCase();
  if (source === "local" || source === "internal") return true;
  return Boolean(room.userId || room.companyId);
};

const guestOptions = [1, 2, 4, 6, 8, 10, 12, 15, 20, 25, 30, 50];

const mockRatingForRoom = (roomId) => {
  const seed = String(roomId || "")
    .split("")
    .reduce((sum, char) => sum + char.charCodeAt(0), 0);
  const rating = 4.2 + (seed % 8) / 10;
  const reviews = 40 + (seed % 120);
  return { rating: Number(rating.toFixed(1)), reviews };
};

const MeetingHome = () => {
  const navigate = useNavigate();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [latestRooms, setLatestRooms] = useState([]);
  const [latestRoomsLoading, setLatestRoomsLoading] = useState(true);
  const [searchLocation, setSearchLocation] = useState("");
  const [searchDate, setSearchDate] = useState(null);
  const [searchGuests, setSearchGuests] = useState("1");
  const [searchSession, setSearchSession] = useState(sessionOptions[0]);
  const [searchDuration, setSearchDuration] = useState(durationOptions[0]);
  const [searchRoomType, setSearchRoomType] = useState(roomTypeOptions[0]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [favorites, setFavorites] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("mh_favorite_rooms") || "[]"));
    } catch {
      return new Set();
    }
  });
  const [recentLocations, setRecentLocations] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("mh_recent_locations") || "[]");
    } catch {
      return [];
    }
  });

  const saveRecentLocation = (location) => {
    const normalized = (location || "").trim();
    if (!normalized) return;
    const updated = [
      normalized,
      ...recentLocations.filter((item) => item.toLowerCase() !== normalized.toLowerCase()),
    ].slice(0, 5);
    setRecentLocations(updated);
    localStorage.setItem("mh_recent_locations", JSON.stringify(updated));
  };

  const handleQuickSearch = () => {
    const params = new URLSearchParams();
    if (searchLocation.trim()) {
      params.set("location", searchLocation.trim());
      saveRecentLocation(searchLocation.trim());
    }
    if (searchDate) params.set("date", format(searchDate, "yyyy-MM-dd"));
    if (searchGuests) params.set("participants", String(searchGuests));
    if (searchRoomType && searchRoomType !== roomTypeOptions[0]) {
      params.set("roomType", searchRoomType);
    }
    if (searchSession) params.set("session", searchSession);
    if (searchDuration) params.set("duration", searchDuration);
    navigate(`/meetings/search-results?${params.toString()}`);
  };

  const handleCityClick = (cityName) => {
    setSearchLocation(cityName);
    saveRecentLocation(cityName);
    navigate(`/meetings/search-results?${new URLSearchParams({ location: cityName }).toString()}`);
  };

  const handleCategoryClick = (roomType) => {
    const params = new URLSearchParams();
    if (roomType) params.set("roomType", roomType);
    navigate(`/meetings/search-results?${params.toString()}`);
  };

  const handleSignInClick = () => {
    const existingRedirect = localStorage.getItem("authRedirectPath");
    if (!existingRedirect) {
      localStorage.setItem("authRedirectPath", "/meeting-home");
    }
    setIsLoginModalOpen(true);
  };

  const toggleFavorite = (roomId, event) => {
    event.stopPropagation();
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      localStorage.setItem("mh_favorite_rooms", JSON.stringify([...next]));
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    setLatestRoomsLoading(true);

    const fetchLatestRooms = async () => {
      try {
        const response = await roomsAPI.getRooms({ sort: "latest" });
        const currentUser = (() => {
          try {
            const raw = localStorage.getItem("user");
            return raw ? mergeUserWithStaffSession(JSON.parse(raw)) : null;
          } catch {
            return null;
          }
        })();
        const apiRooms = filterMeetingRoomsVisibleToUser(response.data || [], currentUser)
          .map((room) => {
            const id = room._id || room.id;
            const { rating, reviews } = mockRatingForRoom(id);
            const imageUrl =
              (Array.isArray(room.images) && room.images.length > 0
                ? room.images[0]
                : room.image) || '';
            const resolvedImage =
              imageUrl.startsWith('http') || imageUrl.startsWith('data:')
                ? imageUrl
                : imageUrl
                  ? getImageUrl(imageUrl)
                  : '';
            return {
              id,
              _id: id,
              roomName: room.name || room.roomName || "Meeting Room",
              city: room.city || room.location?.city || room.location || "Unknown",
              capacity: room.capacity || 0,
              pricePerHour: room.pricePerHour ?? 0,
              rating: room.rating || rating,
              reviews: room.reviews || reviews,
              source: room.source || 'local',
              externalLink: room.externalLink || room.website || null,
              images: resolvedImage ? [resolvedImage] : [],
              createdAt: room.createdAt ? new Date(room.createdAt).getTime() : Date.now(),
            };
          });

        const byId = new Map();
        apiRooms.forEach((room) => {
          const key = room.id || room._id;
          if (!key) return;
          if (!byId.has(key) || (room.createdAt || 0) > (byId.get(key)?.createdAt || 0)) {
            byId.set(key, room);
          }
        });

        // Show the public rooms added in the meeting-room section first, then
        // fall back to external listings only to fill the remaining slots.
        const sortedRooms = Array.from(byId.values()).sort((a, b) => {
          const aAdded = isUserAddedMeetingRoom(a);
          const bAdded = isUserAddedMeetingRoom(b);
          if (aAdded !== bAdded) return aAdded ? -1 : 1;
          return (b.createdAt || 0) - (a.createdAt || 0);
        });
        if (!cancelled) setLatestRooms(sortedRooms.slice(0, 4));
      } catch (error) {
        console.error("Error fetching latest meeting rooms:", error);
        if (!cancelled) setLatestRooms([]);
      } finally {
        if (!cancelled) setLatestRoomsLoading(false);
      }
    };

    fetchLatestRooms();
    const refreshRooms = () => fetchLatestRooms();
    window.addEventListener("storage", refreshRooms);
    window.addEventListener("meetingRoomsUpdated", refreshRooms);

    return () => {
      cancelled = true;
      window.removeEventListener("storage", refreshRooms);
      window.removeEventListener("meetingRoomsUpdated", refreshRooms);
    };
  }, []);

  const spacesToRender = useMemo(() => {
    if (latestRooms.length === 0) return fallbackRecommendedSpaces;
    const fallbackImage =
      "https://images.unsplash.com/photo-1497366216548-37526070297c?w=900&q=80";
    return latestRooms.map((room, index) => ({
      id: room.id || room._id || `room-${index}`,
      roomName: room.roomName || "Meeting Room",
      city: room.city || "Unknown",
      capacity: room.capacity || 0,
      pricePerHour: room.pricePerHour ?? 0,
      rating: room.rating || 4.5,
      reviews: room.reviews || 50,
      image: room.images?.[0] || fallbackImage,
      sourceId: room.id || room._id,
      source: room.source || "local",
      externalLink: room.externalLink || null,
    }));
  }, [latestRooms]);

  const displaySpaces = spacesToRender.slice(0, 4);
  const aiSpaces = spacesToRender.slice(0, 3);

  const openRoom = (space) => {
    const roomKey = space.sourceId || space.id;
    if (space.source === "serpapi" && space.externalLink) {
      window.open(space.externalLink, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(`/meetings/room/${roomKey}`);
  };

  const renderRoomCard = (space) => {
    const roomKey = space.sourceId || space.id;
    const isFavorite = favorites.has(roomKey);
    return (
      <article
        key={space.id}
        className="mh-room-card"
        onClick={() => openRoom(space)}
        onKeyDown={(event) => {
          if (event.key === "Enter") openRoom(space);
        }}
        role="button"
        tabIndex={0}
      >
        <div className="mh-room-image-wrap">
          <img
            className="mh-room-image"
            src={space.image}
            alt={space.roomName}
            {...getExternalImageProps(space.source)}
          />
          <button
            type="button"
            className={`mh-room-favorite ${isFavorite ? "is-active" : ""}`}
            aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
            onClick={(event) => toggleFavorite(roomKey, event)}
          >
            <FaHeart />
          </button>
        </div>
        <div className="mh-room-content">
          <h3>{space.roomName}</h3>
          <p className="mh-room-location">
            <FaMapMarkerAlt /> {space.city}
          </p>
          <div className="mh-room-meta">
            <span>{space.capacity || 10} people</span>
            <span>
              <FaStar className="mh-room-star" /> {space.rating} ({space.reviews})
            </span>
          </div>
          <div className="mh-room-footer">
            <div className="mh-room-price">
              <strong>INR {new Intl.NumberFormat("en-IN").format(space.pricePerHour)}</strong>
              <span>/hour</span>
            </div>
            <button
              type="button"
              className="mh-room-book-btn"
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/meetings/book-flow?roomId=${encodeURIComponent(roomKey)}`);
              }}
            >
              Book Now
            </button>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="meeting-home-page meeting-page-with-bottom-nav">
      <Navbar isScrolled={false} onSignInClick={handleSignInClick} showLoginModal />

      <main className="mh-main">
        <section className="mh-hero2">
          <img className="mh-hero2-media" src={HERO_IMAGE} alt="Modern meeting space" />
          <div className="mh-hero2-overlay" />
          <div className="mh-hero2-content">
            <h1 className="mh-hero2-title">
              Find. Book. <span className="mh-hero2-highlight">Meet.</span>
            </h1>
            <p className="mh-hero2-accent">All in One Place.</p>
            <p className="mh-hero2-subtitle">
              Discover and book professional meeting rooms, conference halls and event spaces in
              minutes.
            </p>
          </div>
        </section>

        <section className="mh-filterbar-wrap">
          <div className="mh-filterbar">
            <div className="mh-filter-field mh-filter-location">
              <label className="mh-filter-label" htmlFor="mh-location">
                <FaMapMarkerAlt /> Location
              </label>
              <input
                id="mh-location"
                className="mh-filter-input"
                type="text"
                placeholder="City or area"
                value={searchLocation}
                onChange={(e) => {
                  setSearchLocation(e.target.value);
                  setShowLocationDropdown(true);
                }}
                onFocus={() => setShowLocationDropdown(true)}
                onBlur={() => setTimeout(() => setShowLocationDropdown(false), 140)}
              />
              {showLocationDropdown && recentLocations.length > 0 && (
                <div className="mh-location-dropdown">
                  <div className="mh-location-dropdown-title">Recent searches</div>
                  {recentLocations.map((location) => (
                    <button
                      key={location}
                      type="button"
                      className="mh-location-dropdown-item"
                      onMouseDown={() => {
                        setSearchLocation(location);
                        setShowLocationDropdown(false);
                      }}
                    >
                      {location}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mh-filter-field">
              <label className="mh-filter-label" htmlFor="mh-date">
                <FaCalendarAlt /> Date
              </label>
              <DatePicker
                id="mh-date"
                selected={searchDate}
                onChange={(date) => setSearchDate(date)}
                placeholderText="Select date"
                dateFormat="MMM dd, yyyy"
                minDate={new Date()}
                className="mh-filter-input mh-date-input"
                popperClassName="mh-datepicker-popper"
                portalId="mh-datepicker-portal"
              />
            </div>

            <div className="mh-filter-field">
              <label className="mh-filter-label" htmlFor="mh-session">
                <FaClock /> Session
              </label>
              <select
                id="mh-session"
                className="mh-filter-input mh-filter-select"
                value={searchSession}
                onChange={(e) => setSearchSession(e.target.value)}
              >
                {sessionOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="mh-filter-field">
              <label className="mh-filter-label" htmlFor="mh-duration">
                <FaClock /> Duration
              </label>
              <select
                id="mh-duration"
                className="mh-filter-input mh-filter-select"
                value={searchDuration}
                onChange={(e) => setSearchDuration(e.target.value)}
              >
                {durationOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="mh-filter-field">
              <label className="mh-filter-label" htmlFor="mh-guests">
                <FaUsers /> Capacity
              </label>
              <select
                id="mh-guests"
                className="mh-filter-input mh-filter-select"
                value={searchGuests}
                onChange={(e) => setSearchGuests(e.target.value)}
              >
                {guestOptions.map((count) => (
                  <option key={count} value={count}>
                    {count} {count === 1 ? "person" : "people"}
                  </option>
                ))}
              </select>
            </div>

            <div className="mh-filter-field">
              <label className="mh-filter-label" htmlFor="mh-roomtype">
                <FaBuilding /> Room Type
              </label>
              <select
                id="mh-roomtype"
                className="mh-filter-input mh-filter-select"
                value={searchRoomType}
                onChange={(e) => setSearchRoomType(e.target.value)}
              >
                {roomTypeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <button type="button" className="mh-filter-search" onClick={handleQuickSearch}>
              Search Spaces
            </button>
          </div>
        </section>

        <button
          type="button"
          className="mh-private-highlight"
          onClick={() => navigate("/meetings/workspace")}
        >
          <span className="mh-private-highlight-icon">
            <FaLock />
          </span>
          <span className="mh-private-highlight-text">
            <span className="mh-private-highlight-title">🔒 Private Rooms</span>
            <span className="mh-private-highlight-desc">
              Book a private meeting space
            </span>
          </span>
          <span className="mh-private-highlight-cta">
            Explore <FaArrowRight />
          </span>
        </button>

        <section className="mh-categories">
          {categoryItems.map((cat) => (
            <button
              key={cat.label}
              type="button"
              className="mh-category"
              onClick={() => handleCategoryClick(cat.type)}
            >
              <span className="mh-category-icon">{cat.icon}</span>
              <span className="mh-category-label">{cat.label}</span>
            </button>
          ))}
          <button
            type="button"
            className="mh-category"
            onClick={() => navigate("/meetings/rooms")}
          >
            <span className="mh-category-icon">
              <FaThLarge />
            </span>
            <span className="mh-category-label">View All</span>
          </button>
        </section>

        <section className="mh-cities">
          <div className="mh-section-head">
            <h2>Popular Cities</h2>
            <button
              type="button"
              className="mh-view-all-button"
              onClick={() => navigate("/meetings/rooms")}
            >
              View all <FaChevronRight />
            </button>
          </div>
          <div className="mh-cities-row">
            {popularCities.map((city) => (
              <button
                key={city}
                type="button"
                className="mh-city-pill"
                onClick={() => handleCityClick(city)}
              >
                <FaMapMarkerAlt /> {city}
              </button>
            ))}
          </div>
        </section>

        <section className="mh-featured">
          <div className="mh-section-head">
            <h2>Featured Meeting Rooms</h2>
            <button
              type="button"
              className="mh-view-all-button"
              onClick={() => navigate("/meetings/rooms")}
            >
              View all <FaChevronRight />
            </button>
          </div>

          {latestRoomsLoading ? (
            <div className="mh-status-message">Loading spaces...</div>
          ) : displaySpaces.length === 0 ? (
            <div className="mh-status-message">
              No meeting rooms listed yet. Hosts can add rooms from their dashboard — public rooms appear here for everyone.
            </div>
          ) : (
            <div className="mh-featured-layout">
              <div className="mh-featured-grid">
                {displaySpaces.map((space) => renderRoomCard(space))}
              </div>
              <aside className="mh-featured-map">
                <iframe
                  title="Meeting rooms map"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=68.7%2C8.4%2C97.25%2C33.5&layer=mapnik"
                  loading="lazy"
                />
                <div className="mh-featured-map-caption">
                  <FaMapMarkerAlt />
                  <span>Spaces across major cities</span>
                </div>
              </aside>
            </div>
          )}
        </section>

        <section className="mh-feature-strip">
          <div className="mh-feature-strip-shell">
            {featureItems.map((item) => (
              <article className="mh-feature-item" key={item.title}>
                <div className="mh-feature-icon">{item.icon}</div>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {aiSpaces.length > 0 && (
          <section className="mh-ai-reco">
            <div className="mh-section-head">
              <h2>AI Recommendations For You</h2>
              <button
                type="button"
                className="mh-view-all-button"
                onClick={() => navigate("/meetings/rooms")}
              >
                View all <FaChevronRight />
              </button>
            </div>
            <div className="mh-ai-grid">{aiSpaces.map((space) => renderRoomCard(space))}</div>
          </section>
        )}

        <section className="mh-offers">
          <div className="mh-section-head">
            <h2>Special Offers</h2>
          </div>
          <div className="mh-offers-row">
            {specialOffers.map((offer) => (
              <div className="mh-offer-card" key={offer.id}>
                <div className="mh-offer-content">
                  <span className="mh-offer-badge">{offer.badge}</span>
                  <h3>{offer.title}</h3>
                  <p>{offer.description}</p>
                  <button
                    type="button"
                    className="mh-offer-btn"
                    onClick={() => navigate("/meetings/rooms")}
                  >
                    Book Now <FaArrowRight />
                  </button>
                </div>
                <div
                  className="mh-offer-media"
                  style={{ backgroundImage: `url(${offer.image})` }}
                />
              </div>
            ))}
            <aside className="mh-contact-card">
              <h3>Have a large team or special needs?</h3>
              <p>Let us help you find the perfect space for your next meeting or event.</p>
              <button type="button" onClick={() => navigate("/help")}>
                Contact our team
              </button>
            </aside>
          </div>
        </section>

        <section className="mh-trusted">
          <p className="mh-trusted-label">Trusted by leading companies</p>
          <div className="mh-trusted-row">
            {trustedCompanies.map((company) => (
              <span className="mh-trusted-logo" key={company}>
                {company}
              </span>
            ))}
          </div>
        </section>
      </main>

      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      <MeetingSpacePopup />
    </div>
  );
};

export default MeetingHome;
